import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Image,
  Tooltip,
  Typography,
} from "antd";
import {
  EditOutlined,
  ReloadOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "../../../config";

const { Search } = Input;
const { Text } = Typography;

const FALLBACK_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='44' height='44'%3E%3Crect width='44' height='44' fill='%23f0f0f0'/%3E%3C/svg%3E";

const StatusDot = ({ active, label, activeColor = "#52c41a" }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: active ? activeColor : "#d9d9d9",
        flexShrink: 0,
        display: "inline-block",
      }}
    />
    <Text style={{ fontSize: 12, color: active ? "#262626" : "#bfbfbf" }}>
      {label}
    </Text>
  </span>
);

const ProductListAllAdmin = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState(undefined);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  const fetchProducts = useCallback(
    async (page = 1, pageSize = 50, search = "", activeFilter = undefined) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        let url =
          global.config.APIURL.BaseURL +
          `api/ProductAdminEdit/GetAllProductsForAdmin?pageNumber=${page}&pageSize=${pageSize}`;
        if (search) url += `&searchText=${encodeURIComponent(search)}`;
        if (activeFilter !== undefined && activeFilter !== null)
          url += `&isActive=${activeFilter}`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProducts(res.data.Products || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.TotalCount,
          current: page,
          pageSize,
        }));
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearch = (value) => {
    setSearchText(value);
    fetchProducts(1, pagination.pageSize, value, isActiveFilter);
  };

  const handleActiveFilter = (value) => {
    const filter =
      value === "all" ? undefined : value === "active" ? true : false;
    setIsActiveFilter(filter);
    fetchProducts(1, pagination.pageSize, searchText, filter);
  };

  const handleTableChange = (pag) => {
    fetchProducts(pag.current, pag.pageSize, searchText, isActiveFilter);
  };

  const columns = [
    {
      title: "Product",
      key: "product",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Image
            src={record.ImageUrl}
            width={44}
            height={44}
            style={{ borderRadius: 8, objectFit: "cover", flexShrink: 0, border: "1px solid #f0f0f0" }}
            fallback={FALLBACK_IMG}
            preview={false}
          />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                color: "#141414",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 220,
              }}
            >
              {record.ProductName}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.DesignNo || "—"}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Rate",
      dataIndex: "SalesRate",
      key: "SalesRate",
      width: 90,
      render: (val) => (
        <Text style={{ fontWeight: 600, fontSize: 13 }}>
          {val ? `₹${val}` : <Text type="secondary">—</Text>}
        </Text>
      ),
    },
    {
      title: "Unit",
      dataIndex: "Unit",
      key: "Unit",
      width: 80,
      render: (val) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "Categories",
      dataIndex: "Categories",
      key: "Categories",
      render: (cats = []) => {
        const visible = cats.slice(0, 2);
        const extra = cats.length - 2;
        return (
          <Space wrap size={4}>
            {visible.map((cat, i) => (
              <Tag
                key={i}
                style={{
                  background: "#f0f5ff",
                  borderColor: "#adc6ff",
                  color: "#2f54eb",
                  fontSize: 11,
                  margin: 0,
                  borderRadius: 4,
                  fontWeight: 500,
                }}
              >
                {cat}
              </Tag>
            ))}
            {extra > 0 && (
              <Tooltip title={cats.slice(2).join(", ")}>
                <Tag
                  style={{
                    background: "#fafafa",
                    borderColor: "#d9d9d9",
                    color: "#8c8c8c",
                    fontSize: 11,
                    margin: 0,
                    cursor: "default",
                    borderRadius: 4,
                  }}
                >
                  +{extra}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, record) => (
        <Space direction="vertical" size={3}>
          <StatusDot
            active={record.IsInStock}
            label={record.IsInStock ? "In Stock" : "Out of Stock"}
            activeColor="#52c41a"
          />
          <StatusDot active={record.IsActive} label="Active" activeColor="#1677ff" />
          <StatusDot active={record.IsPublished} label="Published" activeColor="#722ed1" />
        </Space>
      ),
    },
    {
      title: "",
      key: "action",
      width: 48,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="Edit product">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/AdminProductEdit/${record.ProductID}`)}
            style={{
              color: "#1677ff",
              fontWeight: 500,
              borderRadius: 6,
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: "#f0f5ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppstoreOutlined style={{ color: "#2f54eb", fontSize: 16 }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#141414", lineHeight: 1.3 }}>
              All Products
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {loading ? "Loading…" : `${pagination.total} products total`}
            </Text>
          </div>
        </div>

        <Space wrap size={8}>
          <Search
            placeholder="Name or design no…"
            onSearch={handleSearch}
            allowClear
            style={{ width: 220 }}
          />
          <Select
            defaultValue="all"
            style={{ width: 130 }}
            onChange={handleActiveFilter}
            options={[
              { value: "all", label: "All Status" },
              { value: "active", label: "Active Only" },
              { value: "inactive", label: "Inactive Only" },
            ]}
          />
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchProducts(1, pagination.pageSize, searchText, isActiveFilter)
              }
            />
          </Tooltip>
        </Space>
      </div>

      {/* Table */}
      <Table
        dataSource={products}
        columns={columns}
        rowKey="ProductID"
        loading={loading}
        size="middle"
        scroll={{ x: 760 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ["25", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}–${range[1]} of ${total}`,
          size: "small",
        }}
        onChange={handleTableChange}
        rowClassName={() => "product-table-row"}
        style={{ "--row-hover": "#f6f8ff" }}
      />

      <style>{`
        .product-table-row:hover > td {
          background: #f6f8ff !important;
        }
        .ant-table-thead > tr > th {
          background: #fafafa !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #8c8c8c !important;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          border-bottom: 1px solid #f0f0f0 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f5f5f5 !important;
          padding: 12px 16px !important;
        }
        .ant-table {
          border-radius: 8px !important;
          border: 1px solid #f0f0f0 !important;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ProductListAllAdmin;
